import clsx from 'clsx';
import { useEffect, useState } from 'react';
import { useInterval, useTimeout } from 'usehooks-ts';

const getRandomNumberOrLetter = () => {
  // get random char code except w and m to avoid glitching effect
  const m = 109,
    w = 119;

  const randomCharCode = Math.floor(97 + Math.random() * 26);

  return String.fromCharCode(randomCharCode === m || randomCharCode === w ? randomCharCode + 1 : randomCharCode);
};

type TextDecoderProps = {
  text: string;
  className?: string;
  animationType: 'in' | 'out';
  duration: number;
  delay: number;
  classNames?: string[];
};

const NUMBER_OF_RANDOM_CHARS = 5;

export const TextDecoder: React.FC<TextDecoderProps> = ({
  text,
  className,
  animationType,
  duration,
  delay,
  classNames,
}) => {
  const animateInInterval = Math.floor(duration / text.length);

  const [content, setContent] = useState<string>('');
  const [position, setPosition] = useState<number>(1);

  const [wipePosition, setWipePosition] = useState<number>(0);
  const [paused, setPaused] = useState<boolean>(true);

  useEffect(() => {
    setPaused(true);

    if (animationType === 'in') {
      setWipePosition(0);
      setPosition(1);
    }
  }, [animationType, text.length]);

  useInterval(() => {
    if (paused) return null;

    const code = text
      .substring(Math.max(0, position - NUMBER_OF_RANDOM_CHARS), position)
      .replace(/\w/g, getRandomNumberOrLetter);

    const preCode = text.substring(0, Math.max(position - NUMBER_OF_RANDOM_CHARS, 0));

    const middleCode = (preCode + code).substring(0, position);

    const final = middleCode.substring(0, text.length);

    setContent(final);

    if (animationType === 'out') {
      setWipePosition((value) => Math.min(value + 1, text.length));
      setPosition((value) => Math.max(value - 1, 0));
    } else {
      setPosition((value) => Math.min(value + 1, text.length + NUMBER_OF_RANDOM_CHARS));
    }
  }, animateInInterval);

  useTimeout(() => setPaused(false), paused ? delay : null);

  return (
    <>
      {Array.from(content).map((char, index) => (
        <span
          key={index}
          className={clsx('shrink-0', className, classNames?.[index], {
            'opacity-0': index < wipePosition,
          })}
        >
          {char}
        </span>
      ))}
    </>
  );
};
